import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import { SCHEDULE_QOTD } from "../../../graphql/IslamicQuotes/mutation";
import { GET_SCHEDULED_QOTD } from "../../../graphql/IslamicQuotes/query";
import {
  CATEGORY_LABELS,
  IslamicQuote,
  QuoteOfTheDaySchedule,
} from "../../../graphql/IslamicQuotes/types";
import { useVerseText } from "./quran";

// Local-time YYYY-MM-DD (NOT UTC) — matches how the backend stores/compares
//   the plain date string and what TVs compare against in their own zone.
const todayLocalDateStr = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

type Props = {
  quote: IslamicQuote | null; // null → closed
  onClose: (didSchedule: boolean) => void;
};

const ScheduleQotdDialog: React.FC<Props> = ({ quote, onClose }) => {
  const open = !!quote;
  const [date, setDate] = useState(todayLocalDateStr());
  const [error, setError] = useState<string | null>(null);

  // QURAN quotes carry no stored text — resolve via cmz-backend Quran proxy for the preview.
  const needsFetch = quote?.category === "QURAN" && !quote?.arabicText;
  const { data: verse } = useVerseText(
    needsFetch ? quote?.surahNumber : null,
    needsFetch ? quote?.ayahNumber : null
  );
  const arabic = quote?.arabicText || verse?.arabic || "";
  const translation = quote?.translation || verse?.english || "";

  useEffect(() => {
    if (open) {
      setDate(todayLocalDateStr());
      setError(null);
    }
  }, [open]);

  // Look up whether this exact date is already taken (one QOTD per day).
  const { data: existingData } = useQuery(GET_SCHEDULED_QOTD, {
    variables: { startDate: date, endDate: date },
    skip: !open || !date,
    fetchPolicy: "cache-and-network",
  });
  const existing: QuoteOfTheDaySchedule | undefined =
    existingData?.getScheduledQuotesOfTheDay?.[0];
  // Only warn when the slot holds a *different* quote than the one we're scheduling.
  const willReplace = !!existing && existing.quoteId !== quote?.id;

  const [scheduleQotd, { loading }] = useMutation(SCHEDULE_QOTD);

  const handleConfirm = async () => {
    if (!quote || !date) return;
    setError(null);
    try {
      await scheduleQotd({
        variables: { input: { date, quoteId: quote.id } },
        // Keep the manager's calendar in sync after scheduling.
        refetchQueries: ["GetScheduledQuotesOfTheDay"],
      });
      onClose(true);
    } catch (err: any) {
      setError(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to schedule the Quote of the Day."
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <div className="QV_dialogHeader">
        <Stack direction="row" alignItems="center" gap={1}>
          <StarIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" fontWeight={700}>
            Schedule Quote of the Day
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          size="small"
          onClick={() => !loading && onClose(false)}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers sx={{ p: 3 }}>
        <Stack gap={2}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {quote && (
            <div className="QV_row">
              <Chip
                label={CATEGORY_LABELS[quote.category] || quote.category}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: 11, alignSelf: "flex-start" }}
              />
              <div className="QV_arabic QV_rowArabic">{arabic || "…"}</div>
              {translation && (
                <div className="QV_rowTranslation">{translation}</div>
              )}
              <Typography
                variant="caption"
                sx={{ color: "#6366f1", fontWeight: 700, textAlign: "center" }}
              >
                {quote.reference}
              </Typography>
            </div>
          )}

          <Typography variant="body2" color="text.secondary">
            Pick the date to display this quote on. It publishes to all connected
            TVs (Masjid &amp; Personal) for that day. Only one Quote of the Day
            can be active per date.
          </Typography>

          <TextField
            type="date"
            label="Display date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayLocalDateStr() }}
            fullWidth
          />

          {willReplace && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              A different quote is already scheduled for {date}. Scheduling this
              one will replace it.
            </Alert>
          )}

          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button onClick={() => onClose(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={loading || !date}
              startIcon={<StarIcon />}
              sx={{ borderRadius: 2 }}
            >
              {loading ? "Scheduling…" : "Schedule"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleQotdDialog;
